import React from "react";
import { Link } from "react-router-dom";
import {
  EnvelopeSimple,
  Phone,
  MapPin,
  InstagramLogo,
  LinkedinLogo,
  FacebookLogo,
  XLogo,
  YoutubeLogo,
  ThreadsLogo,
  ArrowUpRight,
  SignIn,
  UserPlus,
} from "@phosphor-icons/react";
import { COMPANY, APP_URLS, SOCIAL_LINKS } from "../../data/site";

const socialIconMap = {
  YoutubeLogo,
  FacebookLogo,
  InstagramLogo,
  ThreadsLogo,
  XLogo,
  LinkedinLogo,
};

const footerSections = [
  {
    title: "Product",
    links: [
      { label: "Services", to: "/services" },
      { label: "Features", to: "/features" },
      { label: "Packages", to: "/packages" },
      { label: "Become a Partner", to: "/become-partner" },
    ],
  },
  {
    title: "Company",
    links: [
      { label: "About Us", to: "/about" },
      { label: "Contact Us", to: "/contact" },
      { label: "Sitemap", to: "/sitemap.xml", staticFile: true },
    ],
  },
  {
    title: "Legal",
    links: [
      { label: "Disclaimer", to: "/disclaimer" },
      { label: "Privacy Policy", to: "/privacy-policy" },
      { label: "Refund Policy", to: "/refund-policy" },
      { label: "Terms & Conditions", to: "/terms-and-conditions" },
      { label: "Merchant Onboarding Policy", to: "/merchant-onboarding-policy" },
      { label: "Merchant Settlement Policy", to: "/merchant-settlement-policy" },
      { label: "Account Deactivation", to: "/account-deactivation" },
    ],
  },
];

export default function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="relative bg-ink900 text-surface mt-32 overflow-hidden"
    >
      <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-brandBlue to-transparent" />
      <div className="absolute -top-32 -right-32 h-72 w-72 rounded-full bg-brandBlue/20 blur-3xl" />
      <div className="absolute -bottom-40 -left-40 h-80 w-80 rounded-full bg-brandOrange/10 blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-10 py-20">
        {/* Top CTA strip */}
        <div className="mb-14 grid lg:grid-cols-12 gap-6 items-center bg-surface/5 border border-surface/15 rounded-3xl p-7 sm:p-10">
          <div className="lg:col-span-7">
            <div className="text-xs uppercase tracking-[0.22em] font-semibold text-brandOrange mb-2">
              Web app live
            </div>
            <div className="font-heading font-extrabold text-2xl sm:text-3xl tracking-tight">
              Login to JayesPay web app or register as a retailer.
            </div>
            <p className="text-sm text-surface/65 mt-2">
              Access your dashboard, wallets, reports and services right from
              your browser.
            </p>
          </div>
          <div className="lg:col-span-5 flex flex-col sm:flex-row gap-3 lg:justify-end">
            <a
              href={APP_URLS.login}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-login-link"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border border-surface/30 text-surface text-sm font-bold hover:bg-surface hover:text-ink900 transition-all"
            >
              <SignIn size={16} weight="bold" />
              Login
            </a>
            <a
              href={APP_URLS.register}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-register-link"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-brandOrange text-ink900 text-sm font-bold hover:bg-brandBlue hover:text-surfaceAlt transition-colors"
            >
              <UserPlus size={16} weight="bold" />
              Register
            </a>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Brand block */}
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-5">
              <img
                src={COMPANY.footerLogo}
                alt={COMPANY.name}
                className="h-16 w-auto max-w-full object-contain"
              />
            </div>

            <p className="text-sm text-surface/70 leading-relaxed max-w-md mb-6">
              <span className="block mb-3 text-surface/90">{COMPANY.attribution}</span>
              Empowering Bharat&apos;s retailers with
              digital payments, banking and assisted services from a single,
              trusted app.
            </p>

            <ul className="space-y-3 text-sm text-surface/80">
              <li className="flex items-start gap-3">
                <MapPin size={18} weight="duotone" className="mt-0.5 text-sky-200 flex-shrink-0" />
                <span data-testid="footer-address">{COMPANY.address}</span>
              </li>
              <li className="flex items-center gap-3">
                <EnvelopeSimple size={18} weight="duotone" className="text-sky-200 flex-shrink-0" />
                <a
                  href={`mailto:${COMPANY.email}`}
                  data-testid="footer-email"
                  className="hover:text-brandOrange transition-colors"
                >
                  {COMPANY.email}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Phone size={18} weight="duotone" className="text-sky-200 flex-shrink-0" />
                <a
                  href={`tel:${COMPANY.phone.replace(/\s/g, "")}`}
                  data-testid="footer-phone"
                  className="hover:text-brandOrange transition-colors"
                >
                  {COMPANY.phone}
                </a>
              </li>
            </ul>

            <div className="mt-7 flex items-center gap-3 flex-wrap">
              {SOCIAL_LINKS.map(({ label, url, icon }) => {
                const Icon = socialIconMap[icon] || XLogo;
                return (
                  <a
                    key={label}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`footer-social-${label.toLowerCase()}`}
                    aria-label={label}
                    className="h-10 w-10 rounded-full border border-surface/15 flex items-center justify-center text-surface/80 hover:bg-brandBlue hover:text-surfaceAlt hover:border-brandBlue transition-all"
                  >
                    <Icon size={18} weight="bold" />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Link columns */}
          <div className="lg:col-span-7 grid grid-cols-2 md:grid-cols-3 gap-8">
            {footerSections.map((section) => (
              <div key={section.title}>
                <div className="text-xs uppercase tracking-[0.22em] text-brandOrange font-bold mb-4">
                  {section.title}
                </div>
                <ul className="space-y-3">
                  {section.links.map((link) => (
                    <li key={link.to}>
                      {link.staticFile ? (
                        <a
                          href={link.to}
                          data-testid={`footer-link-${link.label
                            .toLowerCase()
                            .replace(/\s|&/g, "-")}`}
                          className="group inline-flex items-center gap-1.5 text-sm text-surface/80 hover:text-surfaceAlt transition-colors"
                        >
                          {link.label}
                          <ArrowUpRight
                            size={12}
                            weight="bold"
                            className="opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all"
                          />
                        </a>
                      ) : (
                        <Link
                          to={link.to}
                          data-testid={`footer-link-${link.label
                            .toLowerCase()
                            .replace(/\s|&/g, "-")}`}
                          className="group inline-flex items-center gap-1.5 text-sm text-surface/80 hover:text-surfaceAlt transition-colors"
                        >
                          {link.label}
                          <ArrowUpRight
                            size={12}
                            weight="bold"
                            className="opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all"
                          />
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom strip */}
        <div className="mt-16 pt-8 border-t border-surface/10 flex flex-col md:flex-row gap-4 md:items-center md:justify-between text-xs text-surface/55">
          <div data-testid="footer-copyright">
            © {new Date().getFullYear()} {COMPANY.name}. A product of {COMPANY.legalName}. All rights reserved.
          </div>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1">
            <span>
              CIN:{" "}
              <span className="text-surface/80 font-medium">{COMPANY.cin}</span>
            </span>
            <span>Founded {COMPANY.foundedYear}</span>
            <span className="text-surface/40">Made in Bharat</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
